import { useCallback, useEffect, useRef, useState } from "react";
import { observarClienteAuth, carregarClienteAtual, logoutCliente, clientePrecisaConfirmar } from "../utils/clienteAuth";

const key = loja => `loja_cliente_${loja}`;
function lerPerfil(loja, user) {
  try {
    const perfil = JSON.parse(localStorage.getItem(key(loja)) || "null");
    // Cache não autentica. Só reutilizar após o Firebase confirmar o MESMO uid.
    return perfil?.authUid === user.uid && typeof perfil.uid === "string" ? {...perfil,cadastroId:perfil.cadastroId || perfil.uid,uid:user.uid} : null;
  } catch { return null; }
}
function salvarPerfil(loja, perfil) {
  try { localStorage.setItem(key(loja),JSON.stringify(perfil)); } catch {}
}

export function useClienteSession(loja) {
  const [cliente,setCliente] = useState(null);
  const [loading,setLoading] = useState(true);
  const [error,setError] = useState("");
  const [semPerfil,setSemPerfil] = useState(null);
  // Entrou, mas ainda não confirmou o e-mail com o código: não conta como logado
  const [pendenteConfirmacao,setPendenteConfirmacao] = useState(null);
  const revision = useRef(0), retry = useRef(() => {});

  useEffect(() => {
    const currentRevision = ++revision.current;
    setCliente(null); setSemPerfil(null); setPendenteConfirmacao(null); setError(""); setLoading(true);
    if (!loja) return;
    let disposed = false, userAtual = null;
    const restaurar = async user => {
      if (disposed) return;
      userAtual = user;
      const request = ++revision.current;
      setError(""); setSemPerfil(null); setPendenteConfirmacao(null);
      if (!user) {
        setCliente(null); setLoading(false);
        try { localStorage.removeItem(key(loja)); } catch {}
        return;
      }
      if (clientePrecisaConfirmar(user)) {
        setCliente(null); setPendenteConfirmacao(user); setLoading(false);
        try { localStorage.removeItem(key(loja)); } catch {}
        return;
      }
      const cached = lerPerfil(loja,user);
      setCliente(previous => previous?.authUid === user.uid ? previous : cached);
      setLoading(!cached);
      try {
        const perfil = await carregarClienteAtual(loja);
        if (disposed || request !== revision.current) return;
        if (perfil) { setCliente(perfil); salvarPerfil(loja,perfil); }
        else { setCliente(null); setSemPerfil(user); try { localStorage.removeItem(key(loja)); } catch {} }
      } catch {
        if (disposed || request !== revision.current) return;
        // Falha de rede/perfil NÃO é logout. A sessão Firebase segue válida.
        setError("Sua sessão está ativa, mas não foi possível atualizar seu perfil. Tente novamente.");
      } finally { if (!disposed && request === revision.current) setLoading(false); }
    };
    retry.current = () => restaurar(userAtual);
    const unsub = observarClienteAuth(restaurar);
    const online = () => { if(userAtual) restaurar(userAtual); };
    window.addEventListener("online",online);
    return () => { disposed=true; revision.current=Math.max(revision.current,currentRevision)+1; unsub(); window.removeEventListener("online",online); retry.current=()=>{}; };
  },[loja]);

  const confirmarLogin = useCallback(perfil => {
    ++revision.current; // resposta anterior de restauração não pode desfazer este login
    setCliente(perfil); setLoading(false); setError(""); setSemPerfil(null); setPendenteConfirmacao(null);
    if(loja) salvarPerfil(loja,perfil);
  },[loja]);
  const sair = useCallback(async () => {
    await logoutCliente();
    ++revision.current;
    setCliente(null); setSemPerfil(null); setPendenteConfirmacao(null); setLoading(false); setError("");
    if(loja) { try { localStorage.removeItem(key(loja)); } catch {} }
  },[loja]);
  return {cliente,loading,error,semPerfil,pendenteConfirmacao,confirmarLogin,sair,retry:()=>retry.current()};
}
